import { IPublicClientApplication } from '@azure/msal-browser';
import { dvList, dvCreate, dvUpdate, dvDelete } from '../api/dataverseClient';

// Custom fields — service layer over the pre-existing EAV schema:
// dagrun_pm_customfield (definition: name/type/options/sortorder, project
// lookup) + dagrun_pm_customfieldvalue (one row per task × field, typed
// value columns). The field builder exposes the four spec types; the
// schema's wider type set stays available for future use.

export const CustomFieldType = {
  Text:         100000000,
  Decimal:      100000003,
  Date:         100000005,
  ChoiceSingle: 100000008,
} as const;

export const CUSTOM_FIELD_TYPE_LABEL: Record<number, string> = {
  [CustomFieldType.Text]:         'Text',
  [CustomFieldType.Decimal]:      'Number',
  [CustomFieldType.Date]:         'Date',
  [CustomFieldType.ChoiceSingle]: 'Dropdown',
};

const CUSTOM_FIELD_SCOPE_PROJECT = 100000000;

export interface CustomField {
  dagrun_pm_customfieldid: string;
  dagrun_pm_name: string;
  dagrun_pm_fieldtype: number;
  dagrun_pm_fieldoptionsjson?: string | null;
  dagrun_pm_sortorder?: number | null;
  _dagrun_pm_projectid_value?: string | null;
}

export interface CustomFieldValue {
  dagrun_pm_customfieldvalueid: string;
  _dagrun_pm_fieldid_value: string;
  _dagrun_pm_taskid_value: string;
  dagrun_pm_valuetext?: string | null;
  dagrun_pm_valuenumber?: number | null;
  dagrun_pm_valuedate?: string | null;
  dagrun_pm_valuechoice?: string | null;
}

/** Options list for a dropdown field. Bad/absent JSON → empty list. */
export function fieldOptions(f: CustomField): string[] {
  if (!f.dagrun_pm_fieldoptionsjson) return [];
  try {
    const parsed = JSON.parse(f.dagrun_pm_fieldoptionsjson);
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch { return []; }
}

export async function listProjectCustomFields(
  pca: IPublicClientApplication,
  projectId: string,
): Promise<CustomField[]> {
  return dvList<CustomField>(pca, 'dagrun_pm_customfields', {
    select: ['dagrun_pm_customfieldid', 'dagrun_pm_name', 'dagrun_pm_fieldtype', 'dagrun_pm_fieldoptionsjson', 'dagrun_pm_sortorder', '_dagrun_pm_projectid_value'],
    filter: `_dagrun_pm_projectid_value eq ${projectId}`,
    orderby: 'dagrun_pm_sortorder asc,dagrun_pm_name asc',
  });
}

export async function createCustomField(
  pca: IPublicClientApplication,
  input: { projectId: string; name: string; fieldType: number; options?: string[]; sortOrder: number },
): Promise<string> {
  const body: Record<string, unknown> = {
    dagrun_pm_name: input.name,
    dagrun_pm_fieldtype: input.fieldType,
    dagrun_pm_scope: CUSTOM_FIELD_SCOPE_PROJECT,
    dagrun_pm_sortorder: input.sortOrder,
    'dagrun_pm_projectid@odata.bind': `/dagrun_pm_projects(${input.projectId})`,
  };
  if (input.fieldType === CustomFieldType.ChoiceSingle) {
    body.dagrun_pm_fieldoptionsjson = JSON.stringify(input.options ?? []);
  }
  return dvCreate(pca, 'dagrun_pm_customfields', body);
}

/**
 * Deletes a field definition and every task value stored against it, so no
 * orphaned EAV rows remain. Values first, definition last.
 */
export async function deleteCustomField(
  pca: IPublicClientApplication,
  fieldId: string,
): Promise<void> {
  const values = await dvList<{ dagrun_pm_customfieldvalueid: string }>(pca, 'dagrun_pm_customfieldvalues', {
    select: ['dagrun_pm_customfieldvalueid'],
    filter: `_dagrun_pm_fieldid_value eq ${fieldId}`,
    top: 5000,
  });
  await Promise.all(values.map(v => dvDelete(pca, 'dagrun_pm_customfieldvalues', v.dagrun_pm_customfieldvalueid)));
  await dvDelete(pca, 'dagrun_pm_customfields', fieldId);
}

export async function getTaskCustomFieldValues(
  pca: IPublicClientApplication,
  taskId: string,
): Promise<CustomFieldValue[]> {
  return dvList<CustomFieldValue>(pca, 'dagrun_pm_customfieldvalues', {
    select: ['dagrun_pm_customfieldvalueid', '_dagrun_pm_fieldid_value', '_dagrun_pm_taskid_value', 'dagrun_pm_valuetext', 'dagrun_pm_valuenumber', 'dagrun_pm_valuedate', 'dagrun_pm_valuechoice'],
    filter: `_dagrun_pm_taskid_value eq ${taskId}`,
  });
}

// Which typed value column a field type writes.
function valueColumn(fieldType: number): keyof CustomFieldValue {
  if (fieldType === CustomFieldType.Decimal) return 'dagrun_pm_valuenumber';
  if (fieldType === CustomFieldType.Date) return 'dagrun_pm_valuedate';
  if (fieldType === CustomFieldType.ChoiceSingle) return 'dagrun_pm_valuechoice';
  return 'dagrun_pm_valuetext';
}

/** Read the display value of a value row for a given field definition. */
export function readValue(field: CustomField, value: CustomFieldValue | undefined): string {
  if (!value) return '';
  const col = valueColumn(field.dagrun_pm_fieldtype);
  const raw = value[col];
  if (raw == null) return '';
  if (col === 'dagrun_pm_valuedate') return String(raw).slice(0, 10);
  return String(raw);
}

/**
 * Upsert a task's value for one field. Empty input clears the typed column
 * on the existing row (the row is kept — cheap, and avoids create/delete
 * churn while typing).
 */
export async function upsertTaskCustomFieldValue(
  pca: IPublicClientApplication,
  taskId: string,
  field: CustomField,
  existing: CustomFieldValue | undefined,
  raw: string,
): Promise<string> {
  const col = valueColumn(field.dagrun_pm_fieldtype);
  let typed: unknown = raw.trim() === '' ? null : raw.trim();
  if (typed != null && col === 'dagrun_pm_valuenumber') {
    const n = Number(raw);
    typed = Number.isFinite(n) ? n : null;
  }
  if (existing) {
    await dvUpdate(pca, 'dagrun_pm_customfieldvalues', existing.dagrun_pm_customfieldvalueid, { [col]: typed });
    return existing.dagrun_pm_customfieldvalueid;
  }
  return dvCreate(pca, 'dagrun_pm_customfieldvalues', {
    dagrun_pm_name: field.dagrun_pm_name.slice(0, 100),
    [col]: typed,
    'dagrun_pm_taskid@odata.bind': `/dagrun_pm_tasks(${taskId})`,
    'dagrun_pm_fieldid@odata.bind': `/dagrun_pm_customfields(${field.dagrun_pm_customfieldid})`,
  });
}
